import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  GridOn as PlotIcon,
  CurrencyRupee as RupeeIcon,
  Straighten as SizeIcon,
  LocationOn as LocationIcon,
  Business as SiteIcon,
  Business,
  AccountBalance as BankIcon,
  PhotoCamera as PhotoIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { plotService } from '../../services/plot';
import { showSnackbar } from '../../components/Snackbar';
import { getProjects } from '../../services/project';
import { globalMasterService } from '../../services/stateAPI';
import AlertModal from '../../components/AlertModal';

const projectSuggestions = [
  'Green Meadows',
  'Skyline Apartments',
  'Sunview Park',
  'Modern Residency',
  'Riverside Homes',
  'Eco City',
  'Lake View Villas',
  'Urban Heights'
];

const PlotForm = () => {
  const history = useHistory();
  const location = useLocation();
  const plotData = location.state?.plot;
  const isEditMode = !!plotData;

  const urlParams = new URLSearchParams(location.search);
  const urlProjectId = urlParams.get('projectId');

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '', onConfirm: null });

  const amenitiesOptions = [
    'Swimming Pool',
    'Gym',
    'Park',
    'Children Play Area',
    'Club House',
    'Parking',
    'Security',
    '24/7 Water Supply',
    'Power Backup',
    'Lift',
    'Garden',
    'CCTV Surveillance',
    'Jogging Track',
    'Indoor Games',
    'Community Hall',
  ];

  const [formData, setFormData] = useState({
    projectId: '',
    inventoryType: 'Plot',
    // prefix: '', 
    plotName: '',
    projectName: '',
    siteName: '',
    sector: '',
    block: '',
    width: '',
    length: '',
    totalSize: '',
    facing: '',
    authority: '',
    pricePerSqFt: '',
    companyDiscountPercent: 0,
    discountPerSqFt: 0,
    discountAmount: 0,
    plcPercent: 0,
    amount: '',
    guidelineValue: '',
    plotAmount: '',
    status: '',
    remark: '',
    khasaraNo: '',
    authorityChargesType: '',
    authorityChargesPerSqft: 0,
    totalAuthorityCharges: 0,
  });

  const [plotList, setPlotList] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPlot, setSelectedPlot] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [progressOptions, setProgressOptions] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [facingOptions, setFacingOptions] = useState([]);
  const [loadingFacing, setLoadingFacing] = useState(false);
  const [authorityOptions, setAuthorityOptions] = useState([]);
  const [loadingAuthority, setLoadingAuthority] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchProgressOptions = async () => {
      setLoadingProgress(true);
      try {
        const progressData = await globalMasterService.getProgress();
        // Filter to only include the 4 required status options
        const filteredProgressData = (progressData || []).filter(option => {
          const statusValue = (option.value || option.id || '').toLowerCase();
          const statusLabel = (option.label || '').toLowerCase();

          // Check for our required statuses (case-insensitive, partial matches)
          return statusValue.includes('available') || statusLabel.includes('available') ||
            (statusValue.includes('under') && statusValue.includes('construct')) ||
            (statusLabel.includes('under') && statusLabel.includes('construct')) ||
            statusValue.includes('book') || statusLabel.includes('book') ||
            statusValue.includes('hold') || statusLabel.includes('hold');
        });

        // Normalize the data structure to ensure consistent property names
        const normalizedProgressData = filteredProgressData.map(option => ({
          value: option.value || option.id,
          label: option.label
        }));

        // If we don't have all 4 required options from API, use fallback
        const finalProgressData = normalizedProgressData.length >= 4 ? normalizedProgressData : [
          { value: 'Available', label: 'Available' },
          { value: 'Booked', label: 'Booked' },
          { value: 'underConstruction', label: 'Under Construction' },
          { value: 'Hold', label: 'Hold' },
        ];

        setProgressOptions(finalProgressData);
      } catch (error) {
        console.error('Failed to fetch progress options', error);
        // Fallback options if API fails
        setProgressOptions([
          { value: 'Available', label: 'Available' },
          { value: 'Booked', label: 'Booked' },
          { value: 'underConstruction', label: 'Under Construction' },
          { value: 'Hold', label: 'Hold' },
        ]);
      } finally {
        setLoadingProgress(false);
      }
    };

    fetchProgressOptions();
  }, []);

  useEffect(() => {
    const fetchAuthorityOptions = async () => {
      setLoadingAuthority(true);
      try {
        const API_BASE = process.env.REACT_APP_API_BASE_URL;
        const response = await fetch(
          `${API_BASE}/api/globalMasterDetails?type=authority_charges`,
        );

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const result = await response.json();
        const data = Array.isArray(result.data)
          ? result.data
            .filter(item => item.status === true) // Only active authorities
            .map((item) => ({
              value: item._id,
              label: item.details?.authorityName || 'Unknown Authority',
              perSqFtCharge: item.details?.perSqFtCharge || 0,
            }))
          : [];

        setAuthorityOptions(data);
      } catch (error) {
        console.error('Failed to fetch authority options', error);
        // Fallback options if API fails
        setAuthorityOptions([
          { value: 'local_authority_1', label: 'Local Municipal Authority', perSqFtCharge: 50 },
          { value: 'state_authority_1', label: 'State Housing Authority', perSqFtCharge: 75 },
          { value: 'central_authority_1', label: 'Central Government Authority', perSqFtCharge: 100 },
        ]);
      } finally {
        setLoadingAuthority(false);
      }
    };

    fetchAuthorityOptions();
  }, []);

  useEffect(() => {
    const fetchFacingOptions = async () => {
      setLoadingFacing(true);
      try {
        const data = await globalMasterService.getFacing();
        setFacingOptions(data || []);
      } catch (error) {
        console.error('Failed to fetch facing options', error);
        // Fallback options if API fails
        setFacingOptions([
          { id: 'north', value: 'North', label: 'North' },
          { id: 'south', value: 'South', label: 'South' },
          { id: 'east', value: 'East', label: 'East' },
          { id: 'west', value: 'West', label: 'West' },
          { id: 'north-east', value: 'North-East', label: 'North-East' },
          { id: 'north-west', value: 'North-West', label: 'North-West' },
          { id: 'south-east', value: 'South-East', label: 'South-East' },
          { id: 'south-west', value: 'South-West', label: 'South-West' },
        ]);
      } finally {
        setLoadingFacing(false);
      }
    };

    fetchFacingOptions();
  }, []);

  useEffect(() => {
    if (urlProjectId && projects.length > 0) {
      const project = projects.find(p => p._id === urlProjectId);
      if (project) {
        setFormData(prev => ({
          ...prev,
          projectId: urlProjectId,
          projectName: project.projectName
        }));
      }
    }
  }, [urlProjectId, projects]);

  useEffect(() => {
    if (isEditMode && plotData) {
      // Map authority ID if available, or try to find it by name as fallback
      let authorityId = plotData.authority || '';

      // If we have authority options, verify the ID or try fallback
      if (authorityOptions.length > 0) {
        const idExists = authorityOptions.some(a => a.value === authorityId);

        // If ID doesn't exist or is missing, try matching by label
        if (!idExists || !authorityId) {
          const targetLabel = (plotData.authorityChargesType || plotData.authority || '')
            .toString().trim().toLowerCase();

          if (targetLabel) {
            const found = authorityOptions.find(a =>
              a.label?.toString().trim().toLowerCase() === targetLabel
            );
            if (found) authorityId = found.value;
          }
        }
      }

      setFormData({
        ...formData,
        projectId: plotData.projectId || '',
        inventoryType: plotData.inventoryType || 'Plot',
        plotNo: plotData.plotNo || '',
        plotName: plotData.plotName || '',
        projectName: plotData.projectName || '',
        siteName: plotData.siteName || '',
        sector: plotData.sector || '',
        block: plotData.block || '',
        w1: plotData.w1 || '',
        l1: plotData.l1 || '',
        totalSize: plotData.totalSize || '',
        facing: plotData.facing || '',
        authority: authorityId,
        authorityChargesType: plotData.authorityChargesType || '',
        authorityChargesPerSqft: plotData.authorityChargesPerSqft || 0,
        totalAuthorityCharges: plotData.totalAuthorityCharges || 0,
        pricePerSqFt: plotData.pricePerSqFt || '',
        plcPercent: plotData.plcPercent || '',
        companyDiscountPercent: plotData.companyDiscountPercent || 0,
        discountPerSqFt: (Number(plotData.pricePerSqFt || 0) * (Number(plotData.companyDiscountPercent || 0) / 100)).toFixed(2),
        discountAmount: (Number(plotData.pricePerSqFt || 0) * Number(plotData.totalSize || 0) * (Number(plotData.companyDiscountPercent || 0) / 100)).toFixed(2),
        amount: plotData.amount || '',
        guidelineValue: plotData.guidelineValue || '',
        plotAmount: plotData.plotAmount || '',
        status: plotData.status || '',
        remark: plotData.remark || '',
        khasaraNo: plotData.khasaraNo || '',
      });
    }
  }, [isEditMode, plotData, authorityOptions]);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const data = await getProjects({ projectType: 'Plot' });
      setProjects(data);
    } catch (err) {
      setError('Failed to fetch projects');
      console.error('Error fetching projects:', err);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleAuthoritySelect = (authorityId) => {
    const selected = authorityOptions.find(a => a.value === authorityId);
    setFormData(prev => {
      const perSq = selected ? Number(selected.perSqFtCharge || 0) : 0;
      const totalAuth = perSq * Number(prev.totalSize || 0);
      const currentAmount = Number(prev.amount || 0);
      return {
        ...prev,
        authority: authorityId,
        authorityChargesType: selected ? selected.label : '',
        authorityChargesPerSqft: perSq,
        totalAuthorityCharges: Number(totalAuth.toFixed(2)),
        plotAmount: (currentAmount + totalAuth).toFixed(2),
      };
    });
  };

  const handleFormChange = (field, value) => {
    // Prevent negative values for numeric fields
    if ((field === 'width' || field === 'length' || field === 'totalSize' ||
      field === 'pricePerSqFt' || field === 'companyDiscountPercent' || field === 'plcPercent' || field === 'amount' ||
      field === 'guidelineValue') && value !== '' && parseFloat(value) < 0) {
      return;
    }

    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // Helper to recalculate all derived pricing fields
      if (['width', 'length', 'totalSize', 'pricePerSqFt', 'companyDiscountPercent', 'plcPercent', 'authority', 'discountPerSqFt', 'discountAmount'].includes(field)) {
        const width = Number(updated.width || 0);
        const length = Number(updated.length || 0);
        const pricePerSqFt = Number(updated.pricePerSqFt || 0);
        const plcPercentValue = Number(updated.plcPercent || 0);

        const currentSize = (field === 'totalSize') ? Number(updated.totalSize || 0) : width * length;
        const dimensionStr = (field === 'width' || field === 'length') ? `${width}x${length}` : updated.size;

        let dPerSqFt = Number(updated.discountPerSqFt || 0);
        let dAmount = Number(updated.discountAmount || 0);
        let discountPercent = Number(updated.companyDiscountPercent || 0);

        if (field === 'companyDiscountPercent') {
          dPerSqFt = pricePerSqFt * (discountPercent / 100);
          dAmount = dPerSqFt * currentSize;
        } else if (field === 'discountPerSqFt') {
          dPerSqFt = Number(value);
          dAmount = dPerSqFt * currentSize;
          discountPercent = pricePerSqFt > 0 ? (dPerSqFt / pricePerSqFt) * 100 : 0;
        } else if (field === 'discountAmount') {
          dAmount = Number(value);
          dPerSqFt = currentSize > 0 ? dAmount / currentSize : 0;
          discountPercent = pricePerSqFt > 0 ? (dPerSqFt / pricePerSqFt) * 100 : 0;
        } else {
          // Default: recalculate everything from discountPercent if other fields change
          dPerSqFt = pricePerSqFt * (discountPercent / 100);
          dAmount = dPerSqFt * currentSize;
        }

        const discountedPrice = pricePerSqFt - dPerSqFt;

        const bAmount = discountedPrice * currentSize;
        const pAmount = pricePerSqFt * currentSize * (plcPercentValue / 100);
        const tAmount = bAmount + pAmount;

        const selectedAuth = authorityOptions.find(a => a.value === (field === 'authority' ? value : updated.authority));
        const perSqAuth = selectedAuth ? Number(selectedAuth.perSqFtCharge || 0) : Number(updated.authorityChargesPerSqft || 0);
        const totalAuthCharges = Number((perSqAuth * currentSize).toFixed(2));

        return {
          ...updated,
          totalSize: field === 'totalSize' ? value : (currentSize > 0 ? currentSize.toFixed(2) : updated.totalSize),
          size: dimensionStr,
          companyDiscountPercent: field === 'companyDiscountPercent' ? value : discountPercent.toFixed(2),
          discountPerSqFt: field === 'discountPerSqFt' ? value : dPerSqFt.toFixed(2),
          discountAmount: field === 'discountAmount' ? value : dAmount.toFixed(2),
          amount: tAmount.toFixed(2),
          authorityChargesPerSqft: perSqAuth,
          totalAuthorityCharges: totalAuthCharges,
          authorityChargesType: selectedAuth ? selectedAuth.label : updated.authorityChargesType,
          plotAmount: (tAmount + totalAuthCharges).toFixed(2),
        };
      }

      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.projectId) {
      setError('Please select a project before creating a plot.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const statusValue = (formData.status || '').toLowerCase();

      const payload = {
        projectId: formData.projectId,
        inventoryType: formData.inventoryType || 'Plot',
        // prefix: formData.prefix, 
        plotName: formData.plotName || '',
        width: Number(formData.width),
        length: Number(formData.length),
        totalSize: Number(formData.totalSize),
        facing: formData.facing,
        authority: formData.authority,
        authorityChargesType: formData.authorityChargesType || '',
        authorityChargesPerSqft: Number(formData.authorityChargesPerSqft || 0),
        totalAuthorityCharges: Number(formData.totalAuthorityCharges || 0),
        pricePerSqFt: Number(formData.pricePerSqFt),
        companyDiscountPercent: Number(formData.companyDiscountPercent || 0),
        discountPerSqFt: Number(formData.discountPerSqFt || 0),
        discountAmount: Number(formData.discountAmount || 0),
        plcPercent: Number(formData.plcPercent),
        amount: Number(formData.amount),
        guidelineValue: Number(formData.guidelineValue),
        status: statusValue,
        remark: formData.remark || '',
        khasaraNo: formData.khasaraNo || '',
        plotAmount: Number(formData.plotAmount || 0),
      };

      const response = isEditMode
        ? await plotService.updatePlotInventory(plotData._id || plotData.id, payload)
        : await plotService.createPlotInventory(payload);
      const body = response || {};
      const ok = body.status === true || body.success === true;

      if (ok) {
        const msg = isEditMode ? 'Plot updated successfully!' : 'Plot created successfully!';
        setSuccess(msg);
        try {
          showSnackbar({ type: 'success', message: msg });
        } catch { }

        setAlertModal({
          open: true,
          title: 'Success',
          message: msg,
          onConfirm: () => {
            history.push(`/app/project/PlotListDetails?projectId=${formData.projectId}`);
          }
        });
      } else {
        const msg = isEditMode ? (body.message || 'Failed to update plot') : (body.message || 'Failed to create plot');
        setError(msg);
        try {
          showSnackbar({ type: 'error', message: msg });
        } catch { }
      }
    } catch (err) {
      console.error(isEditMode ? 'Error updating plot:' : 'Error creating plot:', err);
      setError(err.message || (isEditMode ? 'Failed to update plot. Please try again.' : 'Failed to create plot. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    history.push('/app/project/PlotListDetails');
  };

  const handleView = (plot) => {
    setSelectedPlot(plot);
    setViewDialogOpen(true);
  };

  const handleEdit = (plot) => {
    const pricePerSqFt = Number(plot.pricePerSqFt || plot.price || 0);
    const companyDiscountPercent = Number(plot.companyDiscountPercent || 0);
    const width = Number(plot.width || 0);
    const length = Number(plot.length || 0);
    const totalSize = plot.totalSize ? Number(plot.totalSize) : (width * length);

    const calculatedDPerSqFt = pricePerSqFt * (companyDiscountPercent / 100);
    const calculatedDAmount = calculatedDPerSqFt * totalSize;

    setSelectedPlot(plot);
    setEditedData({
      ...plot,
      discountPerSqFt: calculatedDPerSqFt.toFixed(2),
      discountAmount: calculatedDAmount.toFixed(2),
    });
    setEditDialogOpen(true);
  };

  const handleSavePlot = async () => {
    try {
      setLoading(true);
      const response = await plotService.updatePlotInventory(selectedPlot.id, editedData);

      if (response.success) {
        setPlotList(plotList.map(plot =>
          plot.id === selectedPlot.id ? { ...editedData } : plot
        ));
        setEditDialogOpen(false);
        setSelectedPlot(null);
        setEditedData({});
        setSuccess('Plot updated successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to update plot');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseEdit = () => {
    setEditDialogOpen(false);
    setSelectedPlot(null);
    setEditedData({});
  };

  const handleCloseView = () => {
    setViewDialogOpen(false);
    setSelectedPlot(null);
  };

  const handleFieldChange = (field, value) => {
    setEditedData((prev) => {
      const updated = { ...prev, [field]: value };

      if (['width', 'length', 'totalSize', 'pricePerSqFt', 'companyDiscountPercent', 'plcPercent', 'authority', 'discountPerSqFt', 'discountAmount'].includes(field)) {
        const width = Number(updated.width || 0);
        const length = Number(updated.length || 0);
        const pricePerSqFt = Number(updated.pricePerSqFt || 0);
        const plcPercentValue = Number(updated.plcPercent || 0);

        const currentSize = (field === 'totalSize') ? Number(updated.totalSize || 0) : width * length;

        let dPerSqFt = Number(updated.discountPerSqFt || 0);
        let dAmount = Number(updated.discountAmount || 0);
        let discountPercent = Number(updated.companyDiscountPercent || 0);

        if (field === 'companyDiscountPercent') {
          dPerSqFt = pricePerSqFt * (discountPercent / 100);
          dAmount = dPerSqFt * currentSize;
        } else if (field === 'discountPerSqFt') {
          dPerSqFt = Number(value);
          dAmount = dPerSqFt * currentSize;
          discountPercent = pricePerSqFt > 0 ? (dPerSqFt / pricePerSqFt) * 100 : 0;
        } else if (field === 'discountAmount') {
          dAmount = Number(value);
          dPerSqFt = currentSize > 0 ? dAmount / currentSize : 0;
          discountPercent = pricePerSqFt > 0 ? (dPerSqFt / pricePerSqFt) * 100 : 0;
        } else {
          // Default calculation for other triggers
          dPerSqFt = pricePerSqFt * (discountPercent / 100);
          dAmount = dPerSqFt * currentSize;
        }

        const discountedPrice = pricePerSqFt - dPerSqFt;

        const bAmount = discountedPrice * currentSize;
        const pAmount = pricePerSqFt * currentSize * (plcPercentValue / 100);
        const tAmount = bAmount + pAmount;

        const selectedAuth = authorityOptions.find(a => a.value === (field === 'authority' ? value : updated.authority));
        const perSqAuth = selectedAuth ? Number(selectedAuth.perSqFtCharge || 0) : Number(updated.authorityChargesPerSqft || 0);
        const totalAuthCharges = Number((perSqAuth * currentSize).toFixed(2));

        return {
          ...updated,
          totalSize: field === 'totalSize' ? value : (currentSize > 0 ? currentSize.toFixed(2) : updated.totalSize),
          companyDiscountPercent: field === 'companyDiscountPercent' ? value : discountPercent,
          discountPerSqFt: field === 'discountPerSqFt' ? value : dPerSqFt.toFixed(2),
          discountAmount: field === 'discountAmount' ? value : dAmount.toFixed(2),
          amount: tAmount.toFixed(2),
          authorityChargesPerSqft: perSqAuth,
          totalAuthorityCharges: totalAuthCharges,
          authorityChargesType: selectedAuth ? selectedAuth.label : updated.authorityChargesType,
          plotAmount: (tAmount + totalAuthCharges).toFixed(2),
        };
      }

      return updated;
    });
  };

  const handleDeletePlot = async (id) => {
    if (window.confirm("Are you sure you want to delete this plot B?")) {
      try {
        setLoading(true);
        const response = await plotService.deletePlotInventory(id);

        if (response.success) {
          setPlotList(plotList.filter((item) => item.id !== id));
          setSuccess('Plot deleted successfully!');
          setTimeout(() => setSuccess(''), 3000);
        }
      } catch (err) {
        setError(err.message || 'Failed to delete plot');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Available':
        return { backgroundColor: 'rgba(76, 175, 80, 0.12)', color: '#2e7d32' };
      case 'Booked':
        return { backgroundColor: 'rgba(25, 118, 210, 0.12)', color: '#1976d2' };
      case 'underConstruction':
      case 'Under Construction':
        return { backgroundColor: 'rgba(255, 193, 7, 0.12)', color: '#f57c00' };
      case 'Hold':
        return { backgroundColor: 'rgba(244, 67, 54, 0.12)', color: '#d32f2f' };
      default:
        return { backgroundColor: 'rgba(158, 158, 158, 0.12)', color: '#616161' };
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleCancel} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <PlotIcon sx={{ mr: 1, color: 'primary.main', fontSize: { xs: 28, md: 32 } }} />
        <Typography variant="h4" fontWeight="bold" color="primary" sx={{ fontSize: { xs: '0.5rem', md: '1.125rem' } }}>
          {isEditMode ? 'Edit Plot' : 'Add New Plot'}
        </Typography>
      </Box>

      {/* Error and Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Form */}
      <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 2, boxShadow: 3 }}>
        <form onSubmit={handleSubmit}>
          {/* Project Information */}
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#2C3E50', mb: 2 }}>
            Project Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth required>
                <InputLabel>Select Project</InputLabel>
                <Select
                  value={formData.projectId || ''}
                  onChange={(e) => {
                    const selectedProjectId = e.target.value;
                    const project = projects.find(p => p._id === selectedProjectId);
                    handleFormChange('projectId', selectedProjectId);
                    if (project) {
                      handleFormChange('projectName', project.projectName);
                    }
                  }}
                  label="Select Project"
                  disabled={loadingProjects}
                  startAdornment={
                    <InputAdornment position="start">
                      <Business color="primary" />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="">
                    <em>Select a Project</em>
                  </MenuItem>
                  {projects.map((proj) => (
                    <MenuItem key={proj._id} value={proj._id}>
                      {proj.projectName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Plot Information */}
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#2C3E50', mt: 4, mb: 2 }}>
            Plot Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Plot Name"
                value={formData.plotName}
                onChange={(e) => handleFormChange('plotName', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Facing</InputLabel>
                <Select
                  value={formData.facing}
                  onChange={(e) => handleFormChange('facing', e.target.value)}
                  label="Facing"
                  disabled={loadingFacing}
                >
                  <MenuItem value="">
                    {loadingFacing ? 'Loading...' : 'Select Facing'}
                  </MenuItem>
                  {facingOptions.map((option) => (
                    <MenuItem key={option.id} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Khasara No."
                value={formData.khasaraNo}
                onChange={(e) => handleFormChange('khasaraNo', e.target.value)}
                placeholder="Enter khasara number"
              />
            </Grid>

            {/* <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Authority</InputLabel>
                <Select
                  value={formData.authority}
                  onChange={(e) => handleAuthoritySelect(e.target.value)}
                  label="Authority"
                  disabled={loadingAuthority}
                >
                  <MenuItem value="">
                    <em>Select Authority</em>
                  </MenuItem>
                  {
                    console.log('Authority Options:', authorityOptions)
                  }
                  {authorityOptions.map((authority) => (
                    <MenuItem key={authority.value} value={authority.value}>
                      {authority.label} (₹{authority.perSqFtCharge}/sq.ft)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid> */}
          </Grid>

          {/* Plot Dimensions */}
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#2C3E50', mt: 4, mb: 2 }}>
            📐 Plot Dimensions
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Enter width and length to automatically calculate total area and pricing
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                required
                type="number"
                label="Width (feet)"
                value={formData.width}
                onChange={(e) => {
                  const val = e.target.value;
                  // Only allow positive numbers up to 7 digits or empty string
                  if (val === '' || (!isNaN(val) && parseFloat(val) >= 0 && val.replace('.', '').length <= 7)) {
                    handleFormChange('width', val);
                  }
                }}
                onKeyDown={(e) => {
                  // Prevent negative sign, 'e', 'E', '+', '.' (if already present)
                  if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                    e.preventDefault();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SizeIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  maxLength: 7,
                }}

                sx={{
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
              <Typography variant="h5" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                ×
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                required
                type="number"
                label="Length (feet)"
                value={formData.length}
                onChange={(e) => {
                  const val = e.target.value;
                  // Only allow positive numbers up to 7 digits or empty string
                  if (val === '' || (!isNaN(val) && parseFloat(val) >= 0 && val.replace('.', '').length <= 7)) {
                    handleFormChange('length', val);
                  }
                }}
                onKeyDown={(e) => {
                  // Prevent negative sign, 'e', 'E', '+', '.' (if already present)
                  if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                    e.preventDefault();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SizeIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  maxLength: 7,
                }}
                sx={{
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
              <Typography variant="h5" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                =
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                required
                type="number"
                label="Total Size (sq.ft)"
                value={formData.totalSize}
                disabled
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SizeIcon color="success" />
                    </InputAdornment>
                  ),
                }}
                helperText={`Auto-calculated: ${formData.width || 0} × ${formData.length || 0} = ${formData.totalSize || 0} sq.ft`}
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: '#2e7d32',
                    fontWeight: 600,
                  },
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>

            {/* Pricing Details */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                required
                type="number"
                label="Price per sq.ft (₹)"
                value={formData.pricePerSqFt}
                onChange={(e) => handleFormChange('pricePerSqFt', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <RupeeIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Discount Percentage (%)"
                value={formData.companyDiscountPercent !== undefined && formData.companyDiscountPercent !== null && formData.companyDiscountPercent !== '' ? formData.companyDiscountPercent : 0}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || /^\d{0,3}$/.test(val)) {
                    handleFormChange('companyDiscountPercent', val);
                  }
                }}
                onKeyDown={(e) => {
                  if (['-', 'e', 'E', '+'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                sx={{
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Discount per sq.ft (₹)"
                value={formData.discountPerSqFt || 0}
                onChange={(e) => handleFormChange('discountPerSqFt', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <RupeeIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Discount Amount (₹)"
                value={formData.discountAmount || 0}
                onChange={(e) => handleFormChange('discountAmount', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <RupeeIcon color="error" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                required
                type="number"
                label="PLC Percentage (%)"
                value={formData.plcPercent !== undefined && formData.plcPercent !== null && formData.plcPercent !== '' ? formData.plcPercent : 0}
                // onChange={(e) => handleFormChange('plcPercent', e.target.value)}
                onChange={(e) => {
                  const val = e.target.value;
                  // Only allow numbers up to 3 digits
                  if (val === '' || /^\d{0,3}$/.test(val)) {
                    handleFormChange('plcPercent', val);
                  }
                }}
                onKeyDown={(e) => {
                  if (['-', 'e', 'E', '+', '.'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                sx={{
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                required
                type="number"
                label="Amount (₹)"
                value={formData.amount}
                disabled
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <RupeeIcon color="success" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Circle Rate
(₹)"
                value={formData.guidelineValue}
                onChange={(e) => handleFormChange('guidelineValue', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <RupeeIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status || ''}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                  label="Status"
                  disabled={loadingProgress}
                >
                  <MenuItem value="">
                    {loadingProgress ? 'Loading...' : 'Select Status'}
                  </MenuItem>
                  {progressOptions.map((option) => (
                    <MenuItem key={option.id} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Authority</InputLabel>
                <Select
                  value={formData.authority || ''}
                  onChange={(e) => handleFormChange('authority', e.target.value)}
                  label="Authority"
                  disabled={loadingAuthority}
                >
                  <MenuItem value="">
                    <em>Select Authority</em>
                  </MenuItem>
                  {authorityOptions.map((authority) => (
                    <MenuItem key={authority.value} value={authority.value}>
                      {authority.label} (₹{authority.perSqFtCharge}/sq.ft)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Authority Charges (₹)"
                value={formData.totalAuthorityCharges || 0}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <RupeeIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                disabled
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                    fontWeight: 600,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Total Plot Amount (₹)"
                value={formData.plotAmount || 0}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <RupeeIcon color="success" />
                    </InputAdornment>
                  ),
                }}
                disabled
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: '#1b5e20',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                  },
                }}
              />
            </Grid>
          </Grid>


          {/* Additional Information */}
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#2C3E50', mt: 4, mb: 2 }}>
            Additional Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Remark"
                value={formData.remark}
                onChange={(e) => handleFormChange('remark', e.target.value)}
                placeholder="Enter any additional notes or remarks about this plot..."
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={handleCancel}
              sx={{ minWidth: 120, width: { xs: '100%', sm: 'auto' } }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={loading ? <CircularProgress size={20} /> : (isEditMode ? <SaveIcon /> : <AddIcon />)}
              sx={{ minWidth: 120, width: { xs: '100%', sm: 'auto' } }}
              disabled={loading}
            >
              {isEditMode ? (loading ? 'Updating...' : 'Update Plot') : (loading ? 'Adding...' : 'Add Plot')}
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Plot List Table - Same as before but with updated field names */}
      {/* {plotList.length > 0 && (
        <Paper sx={{ mt: 4, borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6" fontWeight="bold" color="primary">
              Added Plots ({plotList.length})
            </Typography>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Plot No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Plot Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Width (ft)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Length (ft)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Total Size (sq.ft)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Facing</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Price/sq.ft</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>PLC%</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Guideline Value</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Remark</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plotList
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((plot) => (
                    <TableRow key={plot.id} hover>
                      <TableCell>
                        <Typography fontWeight="medium" color="primary">
                          {plot.plotNo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="500">
                          {plot.plotName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{plot.width} ft</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{plot.length} ft</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <SizeIcon sx={{ mr: 0.5, fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" fontWeight="500">
                            {plot.totalSize} sq.ft
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={plot.facing}
                          size="small"
                          variant="outlined"
                          color="info"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          ₹{plot.pricePerSqFt}/sq.ft
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${plot.plcPercent}%`}
                          size="small"
                          sx={{ 
                            backgroundColor: '#e3f2fd', 
                            color: '#1976d2',
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <RupeeIcon sx={{ mr: 0.5, fontSize: 16, color: 'success.main' }} />
                          <Typography variant="body2" fontWeight="medium" color="success.main">
                            {formatCurrency(plot.amount)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(plot.guidelineValue)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={plot.status}
                          size="small"
                          style={getStatusStyle(plot.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                          {plot.remark}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Plot">
                            <IconButton
                              color="info"
                              size="small"
                              onClick={() => handleView(plot)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Plot">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleEdit(plot)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Plot">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleDeletePlot(plot.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

         
          <TablePagination
            
            component="div"
            count={plotList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )} */}

      {/* View and Edit Dialogs - Same as before but with updated field names */}
      {/* <Dialog open={viewDialogOpen} onClose={handleCloseView} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            Plot Details - {selectedPlot?.plotNo}
          </Typography>
          <IconButton onClick={handleCloseView} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {selectedPlot && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Plot Number</Typography>
                <Typography variant="body1" fontWeight="500">{selectedPlot.plotNo}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Plot Name</Typography>
                <Typography variant="body1" fontWeight="500">{selectedPlot.plotName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Dimensions (W × L)</Typography>
                <Typography variant="body1" fontWeight="500">{selectedPlot.width} ft × {selectedPlot.length} ft</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Total Size</Typography>
                <Typography variant="body1" fontWeight="500">{selectedPlot.totalSize} sq.ft</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Facing</Typography>
                <Chip label={selectedPlot.facing} size="small" color="info" sx={{ mt: 0.5 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Price per sq.ft</Typography>
                <Typography variant="body1" fontWeight="500">₹{selectedPlot.pricePerSqFt}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">PLC Percentage</Typography>
                <Typography variant="body1" fontWeight="500">{selectedPlot.plcPercent}%</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Amount</Typography>
                <Typography variant="body1" fontWeight="500" color="success.main">{formatCurrency(selectedPlot.amount)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Guideline Value</Typography>
                <Typography variant="body1" fontWeight="500">{formatCurrency(selectedPlot.guidelineValue)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={selectedPlot.status} size="small" style={getStatusStyle(selectedPlot.status)} />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Remark</Typography>
                <Typography variant="body1" fontWeight="500">{selectedPlot.remark}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseView} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog> */}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEdit} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            Edit Plot - {selectedPlot?.plotNo}
          </Typography>
          <IconButton onClick={handleCloseEdit} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {editedData && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Plot Number"
                  value={editedData.plotNo || ''}
                  onChange={(e) => handleFieldChange('plotNo', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Plot Name"
                  value={editedData.plotName || ''}
                  onChange={(e) => handleFieldChange('plotName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Width (feet)"
                  value={editedData.width || ''}
                  onChange={(e) => handleFieldChange('width', e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                      e.preventDefault();
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SizeIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Length (feet)"
                  value={editedData.length || ''}
                  onChange={(e) => handleFieldChange('length', parseFloat(e.target.value))}
                  sx={{
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Total Size (sq.ft)"
                  value={editedData.totalSize || ''}
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Facing</InputLabel>
                  <Select
                    value={editedData.facing || ''}
                    onChange={(e) => handleFieldChange('facing', e.target.value)}
                    label="Facing"
                    disabled={loadingFacing}
                  >
                    <MenuItem value="">
                      {loadingFacing ? 'Loading...' : 'Select Facing'}
                    </MenuItem>
                    {facingOptions.map((option) => (
                      <MenuItem key={option.id} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Price per sq.ft"
                  value={editedData.pricePerSqFt || ''}
                  onChange={(e) => handleFieldChange('pricePerSqFt', parseFloat(e.target.value))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>
                  }}
                  sx={{
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Discount Percentage (%)"
                  value={editedData.companyDiscountPercent || 0}
                  onChange={(e) => handleFieldChange('companyDiscountPercent', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Discount per sq.ft (₹)"
                  value={editedData.discountPerSqFt || 0}
                  onChange={(e) => handleFieldChange('discountPerSqFt', e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Discount Amount (₹)"
                  value={editedData.discountAmount || 0}
                  onChange={(e) => handleFieldChange('discountAmount', e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="PLC Percentage (%)"
                  value={editedData.plcPercent || 0}
                  onChange={(e) => handleFieldChange('plcPercent', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Authority</InputLabel>
                  <Select
                    value={editedData.authority || ''}
                    onChange={(e) => handleFieldChange('authority', e.target.value)}
                    label="Authority"
                    disabled={loadingAuthority}
                  >
                    <MenuItem value="">
                      <em>Select Authority</em>
                    </MenuItem>
                    {authorityOptions.map((authority) => (
                      <MenuItem key={authority.value} value={authority.value}>
                        {authority.label} (₹{authority.perSqFtCharge}/sq.ft)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Amount (₹)"
                  value={editedData.amount || ''}
                  disabled
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                      fontWeight: 600,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Circle Rate (₹)"
                  value={editedData.guidelineValue || ''}
                  onChange={(e) => handleFieldChange('guidelineValue', e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editedData.status || ''}
                    onChange={(e) => handleFieldChange('status', e.target.value)}
                    label="Status"
                    disabled={loadingProgress}
                  >
                    <MenuItem value="">{loadingProgress ? 'Loading...' : 'Select Status'}</MenuItem>
                    {progressOptions.map((option) => (
                      <MenuItem key={option.id} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Remark"
                  value={editedData.remark || ''}
                  onChange={(e) => handleFieldChange('remark', e.target.value)}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseEdit} variant="outlined">Cancel</Button>
          <Button onClick={handleSavePlot} variant="contained" color="primary" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        onClose={() => {
          setAlertModal({ ...alertModal, open: false });
          if (alertModal.onConfirm) {
            alertModal.onConfirm();
          }
        }}
      />
    </Box>
  );
};

export default PlotForm;
